import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FeedbackOutlined,
  HelpOutline,
  KeyboardOutlined,
  Menu as MenuIcon,
  MenuOpen,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AIRequestNotifications from "./AIRequestNotifications";
import { useAuth } from "../context/EnterpriseAuthContext";
import { useWorkspaceLayout } from "../context/WorkspaceLayoutContext";
import { InkyMascot } from "./brand";
import { Kbd } from "./layout/ShortcutsHelpDialog";
import { formatShortcut } from "../hooks/useWorkspaceShortcuts";
import { goToRecordMeeting } from "../utils/navigation";

/** Top bar height (px); pages never need it (main is the scroller below it). */
export const TOPBAR_HEIGHT = 60;

/**
 * Workspace top bar: sidebar toggle, search (opens the command palette), AI jobs, help,
 * "Record meeting" and the Inky toggle. Navigation itself lives in the sidebar (AppSidebar).
 * @param {object} props - Component props.
 * @param {() => void} [props.onOpenFeedback] - Open the Send Feedback dialog.
 * @returns {JSX.Element} Top bar.
 */
function Navbar({ onOpenFeedback }) {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const {
    dock,
    openInky,
    toggleSidebar,
    sidebarCollapsed,
    setMobileNavOpen,
    setCommandPaletteOpen,
    setShortcutsHelpOpen,
    screen,
  } = useWorkspaceLayout();
  const [helpAnchor, setHelpAnchor] = useState(null);
  const isDesktop = screen.isMdUp;

  const toggleNav = () => {
    if (isDesktop) toggleSidebar();
    else setMobileNavOpen(true);
  };
  const navLabel = isDesktop ? (sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar") : "Open navigation";

  return (
    <Box
      component="header"
      sx={{
        flex: "none",
        height: TOPBAR_HEIGHT,
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 1.25 },
        px: { xs: 1, sm: 2, md: 3 },
        borderBottom: "1px solid var(--q-border, #E3E0DA)",
        bgcolor: "var(--q-bg, #F6F5F2)",
        position: "relative",
        zIndex: 6,
      }}
    >
      <Tooltip title={navLabel}>
        <IconButton aria-label={navLabel} onClick={toggleNav} sx={{ color: "var(--q-muted, #5E5A54)" }}>
          {isDesktop && !sidebarCollapsed ? <MenuOpen /> : <MenuIcon />}
        </IconButton>
      </Tooltip>

      {!isDesktop && (
        <Box component="img" src="/QuillAI-Logo.png" alt="Quill AI" sx={{ height: 28, width: "auto", display: "block", mr: 0.5 }} />
      )}

      {/* Search: a button that opens the palette (keeps one search surface). */}
      <Box
        component="button"
        type="button"
        onClick={() => setCommandPaletteOpen(true)}
        aria-label="Search customers, projects and documents"
        aria-keyshortcuts="Control+K Meta+K"
        sx={{
          display: { xs: "none", sm: "flex" },
          flex: "1 1 320px",
          maxWidth: 560,
          minWidth: 0,
          alignItems: "center",
          gap: 1.25,
          height: 40,
          pl: 1.75,
          pr: 1,
          font: "inherit",
          fontSize: 14,
          textAlign: "left",
          color: "var(--q-muted-2, #6B665F)",
          bgcolor: "var(--q-surface, #FFFFFF)",
          border: "1px solid var(--q-border, #E3E0DA)",
          borderRadius: "var(--q-radius-pill, 999px)",
          cursor: "text",
          transition: "border-color 120ms ease",
          "&:hover": { borderColor: "var(--q-border-hover, #C9C4BB)" },
          "&:focus-visible": { outline: "2px solid var(--q-focus-ring, #4F3BD6)", outlineOffset: 2 },
        }}
      >
        <Search sx={{ fontSize: 19 }} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          Search customers, projects, documents
        </Box>
        <Kbd>{formatShortcut("Mod+K")}</Kbd>
      </Box>

      <Box sx={{ flex: 1 }} />

      <Tooltip title="Search">
        <IconButton aria-label="Search" onClick={() => setCommandPaletteOpen(true)} sx={{ display: { xs: "inline-flex", sm: "none" }, color: "var(--q-muted, #5E5A54)" }}>
          <Search />
        </IconButton>
      </Tooltip>

      {authState.isAuthenticated && <AIRequestNotifications />}

      <Tooltip title="Help">
        <IconButton
          aria-label="Help"
          aria-haspopup="menu"
          aria-expanded={helpAnchor ? "true" : undefined}
          onClick={(event) => setHelpAnchor(event.currentTarget)}
          sx={{ color: "var(--q-muted, #5E5A54)" }}
        >
          <HelpOutline />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={helpAnchor}
        open={Boolean(helpAnchor)}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setHelpAnchor(null);
            setShortcutsHelpOpen(true);
          }}
        >
          <ListItemIcon>
            <KeyboardOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Keyboard shortcuts</ListItemText>
          <Typography variant="caption" sx={{ ml: 2, color: "var(--q-muted, #5E5A54)" }}>
            ?
          </Typography>
        </MenuItem>
        {onOpenFeedback && (
          <MenuItem
            onClick={() => {
              setHelpAnchor(null);
              onOpenFeedback();
            }}
          >
            <ListItemIcon>
              <FeedbackOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send feedback</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Button
        onClick={() => goToRecordMeeting(navigate)}
        sx={{
          display: { xs: "none", md: "inline-flex" },
          height: 40,
          px: 2,
          gap: 1,
          flex: "none",
          borderRadius: "var(--q-radius-pill, 999px)",
          border: "1px solid var(--q-border-strong, #D3CFC7)",
          bgcolor: "var(--q-surface, #FFFFFF)",
          color: "var(--q-text, #1C1B19)",
          fontSize: 13.5,
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { bgcolor: "var(--q-hover, #ECEAE5)" },
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "var(--q-danger, #D63B1F)" }} />
        Record meeting
      </Button>

      {/* Opens Inky. While the panel is open it is the one Inky surface (it has its own close
          button and avatar), so this button steps aside instead of repeating the logo. */}
      {!dock.open && (
        <Tooltip title={`Show Inky (${formatShortcut("Mod+J")})`}>
          <Button
            aria-controls="inky-dock"
            aria-label="Show Inky"
            onClick={() => openInky({ focus: true })}
            sx={{
              height: 40,
              minWidth: 40,
              px: { xs: 0.75, sm: 1.5 },
              gap: 0.75,
              flex: "none",
              borderRadius: "var(--q-radius-pill, 999px)",
              textTransform: "none",
              fontSize: 13.5,
              fontWeight: 600,
              color: "var(--q-text-2, #3D3A35)",
              bgcolor: "transparent",
              "&:hover": { bgcolor: "var(--q-hover, #ECEAE5)" },
            }}
          >
            <InkyMascot size={26} decorative animated={false} />
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Inky
            </Box>
          </Button>
        </Tooltip>
      )}
    </Box>
  );
}

export default Navbar;
